/**
 * CommanderDrawer — « Ajouter à la commande » depuis une fiche domaine.
 * Choix des cuvées (millésime + quantité) → ajout aux souhaits de commande (« À commander »).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { achatsApi } from '../../services/api';
import { Drawer } from '../../components/ui/Drawer';
import { Button } from '../../components/ui/kit';
import { WineColorDot } from '../../components/wine/wine';
import { BOTTLE_FORMAT_OPTIONS } from '../../constants';

export default function CommanderDrawer({ open, onClose, domain, cuvees, onSaved }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState(() => Object.fromEntries((cuvees || []).map((c) => [c.id, {
    checked: false, year: c.years?.[c.years.length - 1] || '', format: '750', quantity: 6,
  }])));
  const [busy, setBusy] = useState(false);

  const setRow = (id, patch) => setRows((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
  const selected = (cuvees || []).filter((c) => rows[c.id]?.checked);

  const save = async () => {
    setBusy(true);
    try {
      for (const c of selected) {
        const r = rows[c.id];
        await achatsApi.saveWish({
          cuvee_id: c.id, cuvee_name: c.name, domain_id: domain.id, domain_name: domain.name,
          supplier_name: domain.name, color: c.color, year: r.year, format: r.format, quantity: r.quantity,
        });
      }
      qc.invalidateQueries({ queryKey: ['achats-summary'] });
      qc.invalidateQueries({ queryKey: ['achats-wishes'] });
      toast.success(`${selected.length} souhait(s) ajouté(s) à « À commander »`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message || 'Échec'); } finally { setBusy(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Ajouter à la commande" testId="commander-drawer"
      footer={(
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !selected.length} data-testid="commander-save">
            Ajouter {selected.length > 0 ? `(${selected.length})` : ''}
          </Button>
        </div>
      )}>
      <p className="text-[13px] text-n600 mb-3">{domain?.name} — sélectionnez les cuvées à commander.</p>
      <div className="space-y-2">
        {(cuvees || []).map((c) => {
          const r = rows[c.id];
          return (
            <div key={c.id} className={`vd-card p-2.5 ${r.checked ? 'ring-1 ring-gold' : ''}`} data-testid={`commander-cuvee-${c.id}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={r.checked} onChange={() => setRow(c.id, { checked: !r.checked })}
                  className="accent-[#b68235] w-4 h-4" data-testid={`commander-check-${c.id}`} />
                <WineColorDot color={c.color} size={9} />
                <span className="font-title text-lg flex-1">{c.name}</span>
              </label>
              {r.checked && (
                <div className="grid grid-cols-3 gap-2 mt-2 pl-6">
                  <label className="block"><span className="text-[11px] text-n500">Millésime</span>
                    <input className="vd-input h-8 text-[13px]" value={r.year} onChange={(e) => setRow(c.id, { year: e.target.value })} data-testid={`commander-year-${c.id}`} /></label>
                  <label className="block"><span className="text-[11px] text-n500">Format</span>
                    <select className="vd-input h-8 text-[13px]" value={r.format} onChange={(e) => setRow(c.id, { format: e.target.value })}>
                      {BOTTLE_FORMAT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select></label>
                  <label className="block"><span className="text-[11px] text-n500">Quantité</span>
                    <input type="number" min="1" className="vd-input h-8 text-[13px] nums" value={r.quantity} onChange={(e) => setRow(c.id, { quantity: Number(e.target.value) })} data-testid={`commander-qty-${c.id}`} /></label>
                </div>
              )}
            </div>
          );
        })}
        {!(cuvees || []).length && <p className="text-[13px] text-n500">Aucune cuvée pour ce domaine.</p>}
      </div>
    </Drawer>
  );
}
